import React, { useState } from 'react'
import { useInterval } from 'hooks/useInterval'
import { twoDigits } from 'helper/sessionTimeout'

import { Button as CHButton } from 'components/Button/Button'

import { Modal } from 'components/Modal/Modal'
import { useTranslation } from 'react-i18next'
import { getUserLoggedInState } from 'redux/reducers/users'
import { LanguagePreference } from 'models/LanguagePreference'
import { getDefaultLogoutURL } from 'utils/authUrl'
import i18n from 'i18n/config'
import { useSelector } from 'react-redux'
import events, { Event } from 'pubsub/events'
import { getTimeoutWarningDisplayPeriod } from '../util/eventListenerUtil'
import { createAndEmitLogoutEvent } from 'utils/logoutEvents/create-and-emit-logout-event'

export interface TimeoutWarningModalProps {
  onClose: () => void
}

export const TimeoutWarningModal: React.FC<TimeoutWarningModalProps> = ({
  onClose
}) => {
  // callback Definitions, translations, and signin
  const { t } = useTranslation('common')

  // TODO: This should be `useRef`?
  const contentContainerRef = React.createRef<HTMLElement>()

  const currentLang = i18n.language
  const logoutUrl = getDefaultLogoutURL(currentLang as LanguagePreference, true)

  const isUserLoggedIn = useSelector(getUserLoggedInState)

  const callOnClose = () => {
    if (window.handleGlobalHeaderWarningContinueAction) {
      window.handleGlobalHeaderWarningContinueAction()
    }
    onClose()
  }

  const callEndSession = () => {
    if (window.handleGlobalHeaderTimeout) {
      window.handleGlobalHeaderTimeout()
    }
    onClose()
  }

  // Timer Specific Display Logic
  const [secondsRemaining, setSecondsRemaining] = useState(getTimeoutWarningDisplayPeriod() / 1000)

  const secondsToDisplay = secondsRemaining % 60
  const minutesRemaining = (secondsRemaining - secondsToDisplay) / 60
  const minutesToDisplay = minutesRemaining % 60

  useInterval(
    () => {
      if (secondsRemaining > 0) {
        setSecondsRemaining(secondsRemaining - 1)
      }
    },
    secondsRemaining > 0 ? 1000 : 0
  )
  // TODO - potentially refactor TimeoutLogic and move setTimeout2 code into here as the new state-of-truth-for handling when countdown timer hits 0.

  return (
    <Modal
      backDropClickable
      heading={isUserLoggedIn ? t('timeoutTitleAuth') : t('timeoutTitleUnauth')}
      headingClassNameOverride="ch-u-py--3 ds-text-heading--2xl"
      onClose={() => {
        const timeoutEvent: Event = {
          authenticated: isUserLoggedIn,
          text: 'X',
          category: '',
          activity: 'Button Click',
          url: window.location.href,
          property: 'MAX',
          component: 'Alert Modal',
          title: document.title,
          language: i18n.language,
          sendToTealium: false,
          sendToCoreAPI: true,
          isMessageCenter: false
        }
        events.publish(timeoutEvent)
        callOnClose()
      }
      }
      testid='message-center-modal'
      contentContainerRef={contentContainerRef}
      className='ch-u-dialog__session-timeout-warning'
    >

      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div className='ch-c-dialog__content ch-c-dialog__padding-x my-u-py--2'>
        {isUserLoggedIn &&
          <div data-testid="session-timer-display" className='ds-text-heading--5xl ch-u-mb--10 ch-u-color--medium-gray'>{minutesToDisplay}:{twoDigits(secondsToDisplay)}</div>
        }
        {isUserLoggedIn ? <p data-testid="auth-timeout" className="my-u-m--0">{t('timeoutDescriptionAuth')}</p> : <p data-testid="unauth-timeout" className="my-u-m--0">{t('timeoutDescriptionUnauth')}</p>}
      </div>
      <footer className='ch-c-dialog__footer ch-c-dialog__padding-x ch-c-dialog__padding-y ch-u-mb--10'>
        <div className="ds-u-display--flex ds-u-flex-wrap--wrap">
          <CHButton
            className='ds-c-button ds-c-button--primary ch-u-width--full ch-u-mb--6'
            onClick={() => {
              const timeoutEvent: Event = {
                authenticated: isUserLoggedIn,
                text: 'Continue',
                category: '',
                activity: 'Button Click',
                url: window.location.href,
                property: 'MAX',
                component: 'Alert Modal',
                title: document.title,
                language: i18n.language,
                sendToTealium: false,
                sendToCoreAPI: true,
                isMessageCenter: false
              }
              events.publish(timeoutEvent)

              callOnClose()
            }}
          >
            {t('Continue')}
          </CHButton>
        {isUserLoggedIn
          ? (
            <CHButton
              className='ds-c-button ch-u-width--full'
              onClick={() => {
                const timeoutEvent: Event = {
                  authenticated: isUserLoggedIn,
                  text: 'Log Out',
                  category: '',
                  activity: 'Button Click',
                  url: window.location.href,
                  property: 'MAX',
                  component: 'Alert Modal',
                  title: document.title,
                  language: i18n.language,
                  sendToTealium: false,
                  sendToCoreAPI: true,
                  isMessageCenter: false
                }
                events.publish(timeoutEvent)
                createAndEmitLogoutEvent()
                window.location.assign(logoutUrl)
              }}
              data-testid='timeout-btn-logout'
            >
              {t('Log Out')}
            </CHButton>
            )
          : (
            <CHButton
              className='ds-c-button ch-u-width--full'
              onClick={() => {
                const timeoutEvent: Event = {
                  authenticated: isUserLoggedIn,
                  text: 'Log Out',
                  category: '',
                  activity: 'Button Click',
                  url: window.location.href,
                  property: 'MAX',
                  component: 'Alert Modal',
                  title: document.title,
                  language: i18n.language,
                  sendToTealium: false,
                  sendToCoreAPI: true,
                  isMessageCenter: false
                }
                events.publish(timeoutEvent)

                callEndSession()
              }}
              data-testid='timeout-btn-end-session'
            >
              {t('Start Over')}
            </CHButton>
            )}
        </div>
      </footer>

    </Modal>
  )
}
